import { useMemo, useRef, useState } from 'react';
import {
  Archive,
  CreditCard,
  FileText,
  Loader2,
  Mail,
  ReceiptText,
  Package,
  Plus,
  ShieldCheck,
  Truck,
  Cylinder,
} from 'lucide-react';

import {
  fileToDataUrl,
  useArchiveCustomerDocument,
  useCustomerDocuments,
  usePaymentProfile,
  useRequestAccountInfo,
  useSavePaymentProfile,
  useUploadCustomerDocument,
  type CustomerDocType,
  type PaymentMethodValue,
} from '@/hooks/admin/usePaymentDocs';
import { fmtMirrorDate } from '@/lib/dates';
import { useCustomerGroup } from '@/hooks/admin/useCustomerGroup';

/* ------------------------------------------------------------------ */
/* Shared bits                                                         */
/* ------------------------------------------------------------------ */

const TERMS_OPTIONS = ['Net 30', 'Net 15', 'Due on receipt', 'COD'];

const CATEGORIES: Array<{
  key: 'payment_method_orders' | 'payment_method_rentals' | 'payment_method_tanks';
  label: string;
  desc: string;
  icon: React.ReactNode;
}> = [
  {
    key: 'payment_method_orders',
    label: 'Product & delivery orders',
    desc: 'Beverage orders, deliveries, will-call',
    icon: <Truck size={14} />,
  },
  {
    key: 'payment_method_rentals',
    label: 'Equipment rentals',
    desc: 'Dispensers, machines, leased equipment',
    icon: <Package size={14} />,
  },
  {
    key: 'payment_method_tanks',
    label: 'Gas cylinder rentals',
    desc: 'Cylinder rental fees + gas',
    icon: <Cylinder size={14} />,
  },
];

function selectCls() {
  return 'rounded-lg border border-brix-border bg-brix-surface px-2.5 py-1.5 text-xs text-brix-ink focus:outline-none focus:ring-1 focus:ring-brix-primary';
}

/* ------------------------------------------------------------------ */
/* Payment profile card                                                */
/* ------------------------------------------------------------------ */

export interface AccountFacts {
  qboId: number;
  accountNumber: string | null;
  creditLimit: number | null;
}

/**
 * "Account & payments" — the ONE account/payments section on the customer
 * detail (Overview tab). Merges the old Account facts box (QBO ID /
 * Account # / Terms / Credit limit) with the payment profile so terms and
 * payment setup live in a single clean card (Sky 2026-08-14).
 */
export function PaymentProfileCard({
  customerId,
  facts,
}: {
  customerId: string;
  facts?: AccountFacts;
}) {
  const { data, isLoading, error } = usePaymentProfile(customerId);
  const save = useSavePaymentProfile(customerId);
  const request = useRequestAccountInfo(customerId);
  // Member store of a Master-billing group: terms and the per-category
  // payment methods are the master's settings, synced down. Editing them here
  // silently created a store override — so they are read-only instead.
  const { data: groupInfo } = useCustomerGroup(customerId);
  const masterLock =
    groupInfo?.member_of && groupInfo.member_of.billing_mode === 'master'
      ? groupInfo.member_of.master_name
      : null;

  type ProfileField =
    | 'payment_terms'
    | 'taxable'
    | 'payment_method_orders'
    | 'payment_method_rentals'
    | 'payment_method_tanks';
  const [draft, setDraft] = useState<Partial<Record<ProfileField, string>> | null>(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [note, setNote] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);

  const saved = data?.profile;
  const values = useMemo(
    () => ({
      payment_terms: draft?.payment_terms ?? saved?.payment_terms ?? '',
      taxable: draft?.taxable ?? (saved?.taxable ? 'yes' : 'no'),
      payment_method_orders: draft?.payment_method_orders ?? saved?.payment_method_orders ?? '',
      payment_method_rentals: draft?.payment_method_rentals ?? saved?.payment_method_rentals ?? '',
      payment_method_tanks: draft?.payment_method_tanks ?? saved?.payment_method_tanks ?? '',
    }),
    [saved, draft],
  );

  const dirty =
    !!saved &&
    (values.payment_terms !== (saved.payment_terms ?? '') ||
      values.taxable !== (saved.taxable ? 'yes' : 'no') ||
      values.payment_method_orders !== (saved.payment_method_orders ?? '') ||
      values.payment_method_rentals !== (saved.payment_method_rentals ?? '') ||
      values.payment_method_tanks !== (saved.payment_method_tanks ?? ''));

  // Stripe need reflects what's ON SCREEN so the light answers "if I save
  // this, do they need Stripe?" — a check-only customer never does.
  const stripeNeeded = CATEGORIES.some(
    (c) => values[c.key] === 'card' || values[c.key] === 'ach',
  );
  const stripe = data?.stripe;

  if (isLoading) {
    return (
      <div className="rounded-xl border border-brix-border bg-brix-surface px-5 py-4 text-xs text-brix-muted">
        <Loader2 size={13} className="animate-spin inline mr-1.5" /> Loading payment profile…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="rounded-xl border border-brix-border bg-brix-surface px-5 py-4 text-xs text-red-500">
        Payment profile failed to load{error instanceof Error ? ` — ${error.message}` : ''}.
      </div>
    );
  }

  const stripeLight =
    stripe && stripe.active_methods > 0 ? (
      <div className="flex items-center gap-2 rounded-lg border border-green-600/30 bg-green-500/10 px-3 py-2.5 text-xs text-brix-ink">
        <ShieldCheck size={14} className="shrink-0 text-green-600" />
        <span>
          <span className="font-semibold">Stripe is hooked up</span> — {stripe.active_methods}{' '}
          method{stripe.active_methods === 1 ? '' : 's'} on file:{' '}
          {stripe.methods
            .map((m) => `${m.brand ?? (m.kind === 'ach' ? 'bank' : 'card')} •${m.last4 ?? '????'}`)
            .join(', ')}
          . Matching options are selectable above.
        </span>
      </div>
    ) : stripeNeeded ? (
      <div className="flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-xs text-brix-ink">
        <CreditCard size={14} className="shrink-0 text-amber-600" />
        <span>
          <span className="font-semibold">Not backed by Stripe</span> — a card/bank method is
          selected but nothing is saved in their Stripe wallet. Use the request button below to
          have them set it up.
        </span>
      </div>
    ) : (
      <div className="flex items-center gap-2 rounded-lg border border-brix-border bg-brix-surface-alt px-3 py-2.5 text-xs text-brix-muted">
        <ShieldCheck size={14} className="shrink-0" />
        <span>
          <span className="font-semibold text-brix-ink">Stripe not hooked up</span> — no card or
          bank saved, so the Stripe Card/ACH options are locked. Check and ACH-from-them are always
          available (they need nothing saved); use{' '}
          <span className="font-semibold text-brix-ink">Request payment info</span> below to have
          them add a method.
        </span>
      </div>
    );

  // The Stripe options are a REFLECTION of the customer's actual Stripe
  // wallet — a method type they haven't hooked up can't be selected (Sky
  // 2026-08-14). A legacy value saved before this rule still renders.
  const hasCard = (data.stripe?.methods ?? []).some((m) => m.kind === 'card');
  const hasAch = (data.stripe?.methods ?? []).some((m) => m.kind === 'ach');
  // ⚠ The two ACH entries are DIFFERENT arrangements, not a duplicate. "Bank
  // / ACH (Stripe)" is us debiting a bank they authorized, so it needs a saved
  // method. "ACH from them" is money they push on their own initiative — it
  // needs nothing saved, which is exactly why the customer who refuses to
  // connect an account picks it, and why gating it on the wallet would be
  // wrong. Setting either push option also stands that section's autopay down,
  // server-side.
  const methodOptions = (current: string) =>
    [
      { value: '', label: 'Not set', disabled: false },
      { value: 'check', label: 'Check (they mail it)', disabled: false },
      { value: 'ach_push', label: 'ACH from them (bank bill-pay)', disabled: false },
      {
        value: 'card',
        label: hasCard ? 'Card (Stripe)' : 'Card (no card saved in Stripe yet)',
        disabled: !hasCard && current !== 'card',
      },
      {
        value: 'ach',
        label: hasAch ? 'Bank / ACH (Stripe)' : 'Bank / ACH (no bank saved in Stripe yet)',
        disabled: !hasAch && current !== 'ach',
      },
    ];

  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface px-5 py-4 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-display text-sm font-semibold text-brix-ink">
          Account &amp; payments
          {masterLock ? (
            <span className="ml-2 text-[11px] font-normal text-brix-muted">
              🔒 terms &amp; payment methods managed on {masterLock}
            </span>
          ) : null}
        </h2>
        <div className="flex items-center gap-2">
          <label className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight">
            Terms
          </label>
          <select
            className={selectCls()}
            value={values.payment_terms}
            disabled={!!masterLock}
            title={masterLock ? `Managed on ${masterLock} (Master billing)` : undefined}
            onChange={(e) => setDraft((d) => ({ ...(d ?? {}), payment_terms: e.target.value }))}
          >
            {!TERMS_OPTIONS.includes(values.payment_terms) && values.payment_terms ? (
              <option value={values.payment_terms}>{values.payment_terms}</option>
            ) : null}
            <option value="">Not set</option>
            {TERMS_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
      </div>

      {facts ? (
        <dl className="grid grid-cols-3 gap-4 text-sm rounded-lg border border-brix-border bg-brix-surface-alt px-3 py-2.5">
          <div>
            <dt className="text-[10px] uppercase tracking-wide text-brix-muted font-semibold">QBO ID</dt>
            <dd className="font-mono text-xs text-brix-ink mt-0.5">#{facts.qboId}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wide text-brix-muted font-semibold">Account #</dt>
            <dd className="font-mono text-xs text-brix-ink mt-0.5">{facts.accountNumber ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wide text-brix-muted font-semibold">Credit limit</dt>
            <dd className="text-xs text-brix-ink mt-0.5">
              {facts.creditLimit != null
                ? facts.creditLimit.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                : '—'}
            </dd>
          </div>
        </dl>
      ) : null}

      <div className="divide-y divide-brix-border overflow-hidden rounded-lg border border-brix-border">
        {CATEGORIES.map((c) => (
          <div
            key={c.key}
            className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-3 py-2.5 bg-brix-surface"
          >
            <div className="flex items-center gap-2 sm:w-72 shrink-0">
              <span className="text-brix-muted">{c.icon}</span>
              <div>
                <div className="text-sm font-semibold text-brix-ink">{c.label}</div>
                <div className="text-[11px] text-brix-muted leading-tight">{c.desc}</div>
              </div>
            </div>
            <select
              className={selectCls()}
              value={values[c.key]}
              disabled={!!masterLock}
              title={masterLock ? `Managed on ${masterLock} (Master billing)` : undefined}
              onChange={(e) => setDraft((d) => ({ ...(d ?? {}), [c.key]: e.target.value }))}
            >
              {methodOptions(values[c.key]).map((o) => (
                <option key={o.value} value={o.value} disabled={o.disabled}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      {data.qbo ? (
        <div className="flex items-start gap-2 rounded-lg border border-brix-border bg-brix-surface-alt px-3 py-2.5 text-xs">
          <span className="mt-px shrink-0 rounded bg-[#2CA01C]/10 px-1.5 py-0.5 text-[10px] font-bold text-[#2CA01C]">
            QB
          </span>
          <div className="text-brix-muted leading-relaxed">
            <span className="font-semibold text-brix-ink">QuickBooks says:</span>{' '}
            Terms <span className="font-semibold text-brix-ink">{data.qbo.terms ?? 'not set'}</span>
            {' · '}Preferred payment method{' '}
            <span className="font-semibold text-brix-ink">
              {data.qbo.payment_method ?? 'not set'}
            </span>
            {data.qbo.terms && data.qbo.terms !== values.payment_terms ? (
              <>
                {' — '}
                <span className="text-amber-600 font-semibold">differs from the profile terms</span>{' '}
                <button
                  type="button"
                  onClick={() =>
                    setDraft((d) => ({ ...(d ?? {}), payment_terms: data.qbo!.terms ?? '' }))
                  }
                  className="underline text-brix-primary font-semibold"
                >
                  Match QuickBooks
                </button>
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Sales tax.

          The bill-to is NOT here, deliberately (Sky, 2026-08-31: "it should be
          a location setting… so move it"). It is a LOCATION (`is_billing`)
          chosen on the Locations tab, where Service Fusion's and QuickBooks'
          addresses are already listed and importable. Even a READ-ONLY copy
          here would be a second screen asserting one fact, which is the drift
          this whole change removes; a customer with no bill-to is reported by
          the drift check on /admin/payments. */}
      <div className="rounded-lg border border-brix-border bg-brix-surface-alt px-3 py-3 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <ReceiptText size={13} className="text-brix-muted" />
            <h3 className="text-xs font-semibold text-brix-ink">Sales tax</h3>
          </div>
          <label className="flex items-center gap-2 text-xs text-brix-ink cursor-pointer">
            <input
              type="checkbox"
              className="accent-brix-primary cursor-pointer"
              checked={values.taxable === 'yes'}
              onChange={(e) =>
                setDraft((d) => ({ ...(d ?? {}), taxable: e.target.checked ? 'yes' : 'no' }))
              }
            />
            {values.taxable === 'yes' ? 'Taxable' : 'Non-taxable'}
          </label>
        </div>
        <p className="text-[11px] text-brix-muted -mt-1.5">
          Most accounts here are non-taxable (resale). Pushed to QuickBooks on save — pair a
          taxable-exempt account with a current resale certificate in Account documents.
        </p>

      </div>

      {stripeLight}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          disabled={!dirty || save.isPending}
          onClick={() =>
            save.mutate(
              {
                payment_terms: values.payment_terms || null,
                taxable: values.taxable === 'yes',
                payment_method_orders: (values.payment_method_orders || null) as PaymentMethodValue,
                payment_method_rentals: (values.payment_method_rentals ||
                  null) as PaymentMethodValue,
                payment_method_tanks: (values.payment_method_tanks || null) as PaymentMethodValue,
              },
              { onSuccess: () => setDraft(null) },
            )
          }
          className="inline-flex items-center gap-1.5 rounded-lg bg-brix-primary px-3.5 py-2 text-xs font-semibold text-white disabled:opacity-40"
        >
          {save.isPending ? <Loader2 size={12} className="animate-spin" /> : null}
          Save payment profile
        </button>
        <button
          type="button"
          onClick={() => {
            setRequestOpen((v) => !v);
            setSentTo(null);
          }}
          className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500/60 bg-amber-500/10 px-3.5 py-2 text-xs font-semibold text-amber-700"
        >
          <Mail size={13} /> Request payment info…
        </button>
      </div>
      {save.error ? (
        <p className="text-[11px] text-red-500">{(save.error as Error).message}</p>
      ) : null}
      {/* The portal is the record, so a downstream refusal is NOT a failed
          save — it is a thing to fix in Service Fusion or QuickBooks. Say so
          plainly rather than showing it as an error. */}
      {(save.data?.push_notes?.length ?? 0) > 0 ? (
        <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-3 py-2 space-y-1">
          <p className="text-[11px] font-semibold text-amber-700">
            Saved here. Not everything reached the other systems:
          </p>
          {save.data!.push_notes!.map((n) => (
            <p key={n} className="text-[11px] text-amber-700">
              · {n}
            </p>
          ))}
        </div>
      ) : null}

      {requestOpen ? (
        <div className="rounded-lg border border-brix-border bg-brix-surface-alt px-3 py-3 space-y-2">
          <p className="text-xs text-brix-muted leading-relaxed">
            Sends the branded <span className="font-semibold text-brix-ink">“We’re missing your
            payment information”</span> email to{' '}
            <span className="font-semibold text-brix-ink">{data.billing_email ?? '(no Primary email!)'}</span>{' '}
            with a link to set up their card/bank in the portal. The email names the categories
            currently set to card/ACH.
          </p>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note to include in the email…"
            rows={2}
            className="w-full rounded-lg border border-brix-border bg-brix-surface px-2.5 py-1.5 text-xs text-brix-ink focus:outline-none focus:ring-1 focus:ring-brix-primary"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={request.isPending || !data.billing_email}
              onClick={() =>
                request.mutate(
                  { kind: 'payment_info', note: note.trim() || null },
                  {
                    onSuccess: (res) => {
                      setSentTo(res.sent_to);
                      setNote('');
                    },
                  },
                )
              }
              className="inline-flex items-center gap-1.5 rounded-lg bg-brix-primary px-3.5 py-2 text-xs font-semibold text-white disabled:opacity-40"
            >
              {request.isPending ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
              Send email
            </button>
            {sentTo ? (
              <span className="text-[11px] font-semibold text-green-600">Sent to {sentTo} ✓</span>
            ) : null}
            {request.error ? (
              <span className="text-[11px] text-red-500">{(request.error as Error).message}</span>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Documents card (tax ID / resale certificate vault)                  */
/* ------------------------------------------------------------------ */

const DOC_TYPE_LABEL: Record<CustomerDocType, string> = {
  tax_id: 'Tax ID',
  resale_cert: 'Resale certificate',
  other: 'Other',
};

function expiryStatus(expiresOn: string | null): 'expired' | 'expiring' | 'ok' | null {
  if (!expiresOn) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(expiresOn);
  if (!m) return null;
  const exp = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  if (exp < today) return 'expired';
  if (exp - today <= 60 * 24 * 60 * 60 * 1000) return 'expiring';
  return 'ok';
}

export function CustomerDocumentsCard({ customerId }: { customerId: string }) {
  const { data, isLoading } = useCustomerDocuments(customerId);
  const upload = useUploadCustomerDocument(customerId);
  const archive = useArchiveCustomerDocument(customerId);
  const request = useRequestAccountInfo(customerId);

  const [addOpen, setAddOpen] = useState(false);
  const [docType, setDocType] = useState<CustomerDocType>('tax_id');
  const [docNumber, setDocNumber] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const [requestOpen, setRequestOpen] = useState(false);
  const [wantTaxId, setWantTaxId] = useState(false);
  const [wantResale, setWantResale] = useState(true);
  const [reqNote, setReqNote] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);

  const docs = data?.documents ?? [];

  const submitUpload = async () => {
    let fileInput_: { name: string; data: string } | null = null;
    if (file) fileInput_ = { name: file.name, data: await fileToDataUrl(file) };
    upload.mutate(
      {
        doc_type: docType,
        doc_number: docNumber.trim() || null,
        expires_on: expiresOn || null,
        notes: notes.trim() || null,
        file: fileInput_,
      },
      {
        onSuccess: () => {
          setDocNumber('');
          setExpiresOn('');
          setNotes('');
          setFile(null);
          if (fileInput.current) fileInput.current.value = '';
          setAddOpen(false);
        },
      },
    );
  };

  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface px-5 py-4 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-display text-sm font-semibold text-brix-ink flex items-center gap-2">
          <FileText size={15} /> Account documents
        </h2>
        <button
          type="button"
          onClick={() => setAddOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-brix-border px-3 py-1.5 text-xs font-semibold text-brix-ink"
        >
          <Plus size={13} /> Add document
        </button>
      </div>
      <p className="text-[11px] text-brix-muted -mt-1.5">
        Everything on file for this account — the ONE place they live. Tax IDs, resale
        certificates, and a PDF of the signed account application itself are pulled in here
        automatically once an application is accepted; ownership changes and expired certs mean
        these get re-collected, and archived versions stay on record. A customer whose
        application predates this can be topped up from the Onboarding tab (“Copy details onto
        the customer”).
      </p>

      {addOpen ? (
        <div className="rounded-lg border border-brix-border bg-brix-surface-alt px-3 py-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <div>
            <label className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight">
              Type
            </label>
            <select
              className={`${selectCls()} w-full mt-1`}
              value={docType}
              onChange={(e) => setDocType(e.target.value as CustomerDocType)}
            >
              <option value="tax_id">Tax ID</option>
              <option value="resale_cert">Resale certificate</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight">
              Number (EIN / cert #)
            </label>
            <input
              value={docNumber}
              onChange={(e) => setDocNumber(e.target.value)}
              placeholder="e.g. 12-3456789"
              className={`${selectCls()} w-full mt-1`}
            />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight">
              Expires (optional)
            </label>
            <input
              type="date"
              value={expiresOn}
              onChange={(e) => setExpiresOn(e.target.value)}
              className={`${selectCls()} w-full mt-1`}
            />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight">
              File (PDF or photo, optional)
            </label>
            <input
              ref={fileInput}
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/webp"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="w-full mt-1 text-xs text-brix-muted file:mr-2 file:rounded-lg file:border-0 file:bg-brix-primary file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white"
            />
          </div>
          <div className="sm:col-span-2">
            <input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes (optional)"
              className={`${selectCls()} w-full`}
            />
          </div>
          <div className="sm:col-span-2 flex items-center gap-2">
            <button
              type="button"
              disabled={upload.isPending || (!file && !docNumber.trim())}
              onClick={() => void submitUpload()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brix-primary px-3.5 py-2 text-xs font-semibold text-white disabled:opacity-40"
            >
              {upload.isPending ? <Loader2 size={12} className="animate-spin" /> : null}
              Save document
            </button>
            <span className="text-[11px] text-brix-muted">
              Attach a file, enter the number, or both.
            </span>
            {upload.error ? (
              <span className="text-[11px] text-red-500">{(upload.error as Error).message}</span>
            ) : null}
          </div>
        </div>
      ) : null}

      {isLoading ? (
        <div className="text-xs text-brix-muted">
          <Loader2 size={13} className="animate-spin inline mr-1.5" /> Loading documents…
        </div>
      ) : docs.length === 0 ? (
        <div className="rounded-lg border border-dashed border-brix-border px-3 py-4 text-center text-xs text-brix-muted">
          No documents on file yet — no tax ID or resale certificate saved for this customer.
        </div>
      ) : (
        <div className="divide-y divide-brix-border overflow-hidden rounded-lg border border-brix-border">
          {docs.map((doc) => {
            const status = expiryStatus(doc.expires_on);
            return (
              <div
                key={doc.id}
                className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3 px-3 py-2.5 bg-brix-surface"
              >
                <span
                  className={`inline-flex w-fit items-center rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                    doc.doc_type === 'tax_id'
                      ? 'bg-brix-primary/10 text-brix-primary'
                      : doc.doc_type === 'resale_cert'
                        ? 'bg-violet-500/10 text-violet-600'
                        : 'bg-brix-surface-alt text-brix-muted'
                  }`}
                >
                  {DOC_TYPE_LABEL[doc.doc_type]}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-brix-ink truncate">
                    {doc.doc_number ?? doc.label ?? doc.file_name ?? '—'}
                  </div>
                  <div className="text-[11px] text-brix-muted truncate">
                    {doc.file_url ? (
                      <a
                        href={doc.file_url}
                        target="_blank"
                        rel="noreferrer"
                        className="underline text-brix-primary"
                      >
                        {doc.file_name ?? 'View file'}
                      </a>
                    ) : (
                      'No file — number only'
                    )}
                    {doc.notes ? <> · {doc.notes}</> : null}
                  </div>
                </div>
                <div className="text-[11px] whitespace-nowrap">
                  {doc.expires_on ? (
                    <span
                      className={
                        status === 'expired'
                          ? 'font-bold text-red-600'
                          : status === 'expiring'
                            ? 'font-bold text-amber-600'
                            : 'text-brix-muted'
                      }
                    >
                      {status === 'expired' ? 'EXPIRED ' : status === 'expiring' ? 'Expiring ' : 'Exp '}
                      {fmtMirrorDate(doc.expires_on)}
                    </span>
                  ) : (
                    <span className="text-brix-muted">No expiration</span>
                  )}
                </div>
                <button
                  type="button"
                  title="Archive (kept on record, hidden from this list)"
                  disabled={archive.isPending}
                  onClick={() => {
                    if (window.confirm('Archive this document? It stays on record but leaves this list.')) {
                      archive.mutate(doc.id);
                    }
                  }}
                  className="text-brix-muted hover:text-brix-ink"
                >
                  <Archive size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="pt-1 border-t border-brix-border">
        <button
          type="button"
          onClick={() => {
            setRequestOpen((v) => !v);
            setSentTo(null);
          }}
          className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-amber-500/60 bg-amber-500/10 px-3.5 py-2 text-xs font-semibold text-amber-700"
        >
          <Mail size={13} /> Request missing documents…
        </button>
        {requestOpen ? (
          <div className="mt-2 rounded-lg border border-brix-border bg-brix-surface-alt px-3 py-3 space-y-2">
            <p className="text-xs text-brix-muted leading-relaxed">
              Sends the branded <span className="font-semibold text-brix-ink">“We’re missing some
              of your account information”</span> email asking them to reply with the selected
              document(s) — resale-cert requests explain we must charge sales tax until it’s on
              file.
            </p>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-1.5 text-xs text-brix-ink cursor-pointer">
                <input
                  type="checkbox"
                  checked={wantTaxId}
                  onChange={(e) => setWantTaxId(e.target.checked)}
                  className="accent-brix-primary cursor-pointer"
                />
                Tax ID
              </label>
              <label className="flex items-center gap-1.5 text-xs text-brix-ink cursor-pointer">
                <input
                  type="checkbox"
                  checked={wantResale}
                  onChange={(e) => setWantResale(e.target.checked)}
                  className="accent-brix-primary cursor-pointer"
                />
                Resale certificate
              </label>
            </div>
            <textarea
              value={reqNote}
              onChange={(e) => setReqNote(e.target.value)}
              placeholder="Optional note to include in the email…"
              rows={2}
              className="w-full rounded-lg border border-brix-border bg-brix-surface px-2.5 py-1.5 text-xs text-brix-ink focus:outline-none focus:ring-1 focus:ring-brix-primary"
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={request.isPending || (!wantTaxId && !wantResale)}
                onClick={() => {
                  const doc_types: Array<'tax_id' | 'resale_cert'> = [];
                  if (wantTaxId) doc_types.push('tax_id');
                  if (wantResale) doc_types.push('resale_cert');
                  request.mutate(
                    { kind: 'documents', doc_types, note: reqNote.trim() || null },
                    {
                      onSuccess: (res) => {
                        setSentTo(res.sent_to);
                        setReqNote('');
                      },
                    },
                  );
                }}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brix-primary px-3.5 py-2 text-xs font-semibold text-white disabled:opacity-40"
              >
                {request.isPending ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Mail size={12} />
                )}
                Send email
              </button>
              {sentTo ? (
                <span className="text-[11px] font-semibold text-green-600">Sent to {sentTo} ✓</span>
              ) : null}
              {request.error ? (
                <span className="text-[11px] text-red-500">{(request.error as Error).message}</span>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
